/**
 * Exam instructions screen component.
 *
 * Displays exam structure, rules, microphone status, and total duration
 * before starting the simulation. Matches the new frontend interface design.
 *
 * @module InstructionsScreen
 */

import { ArrowLeft, AlertCircle, Play, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  calculateTotalDuration,
  filterSettingsByModules,
  formatDuration,
} from "@/utils/examSimulation";
import type {
  SimulationSettings,
  ExamModule,
  ExamType,
  CEFRLevel,
} from "@/types/exam-simulation";
import type { MicrophonePermissionState } from "@/hooks/useMicrophonePermission";

interface InstructionsScreenProps {
  readonly examType: ExamType;
  readonly level: CEFRLevel;
  readonly selectedModules: ExamModule[];
  readonly settings: SimulationSettings;
  readonly microphonePermission: MicrophonePermissionState;
  readonly onBack: () => void;
  readonly onStartSimulation: () => void;
  readonly isLoading?: boolean;
}

export function InstructionsScreen({
  examType,
  level,
  selectedModules,
  settings,
  microphonePermission,
  onBack,
  onStartSimulation,
  isLoading = false,
}: InstructionsScreenProps) {
  const { t } = useTranslation();
  // Filter settings based on selected modules
  const filteredSettings = filterSettingsByModules(settings, selectedModules);

  // Calculate total duration
  const totalDuration = calculateTotalDuration(settings, selectedModules);

  // Check if speaking module is selected (requires microphone)
  const hasSpeakingModule = selectedModules.includes("speaking");

  // Helper function to determine microphone status indicator styling
  const getMicrophoneStatusIndicatorClass = (
    permission: MicrophonePermissionState,
  ): string => {
    if (permission === "granted") {
      return "bg-success shadow-[0_0_8px_hsl(var(--success)/0.4)]";
    }
    if (permission === "denied") {
      return "bg-destructive shadow-[0_0_8px_hsl(var(--destructive)/0.4)]";
    }
    return "bg-warning shadow-[0_0_8px_hsl(var(--warning)/0.4)]";
  };

  // Helper function to determine microphone status badge styling
  const getMicrophoneStatusBadgeClass = (
    permission: MicrophonePermissionState,
  ): string => {
    if (permission === "granted") {
      return "text-success bg-success/10 border-success/20";
    }
    if (permission === "denied") {
      return "text-destructive bg-destructive/10 border-destructive/20";
    }
    return "text-warning bg-warning/10 border-warning/20";
  };

  // Helper function to get the microphone status text
  const getMicrophoneStatusText = (
    permission: MicrophonePermissionState,
  ): string => {
    if (permission === "granted") {
      return t("simulation.instructions.ready");
    }
    if (permission === "denied") {
      return t("simulation.instructions.accessDenied");
    }
    return t("simulation.instructions.checking");
  };

  // Helper function to get the microphone description text
  const getMicrophoneDescriptionText = (
    permission: MicrophonePermissionState,
  ): string => {
    if (permission === "granted") {
      return t("simulation.instructions.microphoneGranted");
    }
    if (permission === "denied") {
      return t("simulation.instructions.microphoneDenied");
    }
    return t("simulation.instructions.checking");
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex items-center gap-3 sm:gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="gap-2 h-9 px-2 sm:px-3"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">{t("common.back")}</span>
        </Button>
        <div className="min-w-0">
          <h2 className="text-xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2 truncate">
            {t("simulation.instructions.titleWithLevel", {
              examType: t(`examSelection.types.${examType}.name`),
              level,
            })}
          </h2>
          <p className="text-xs sm:text-base text-muted-foreground truncate">
            {t("simulation.instructions.reviewInstructions")}
          </p>
        </div>
      </div>

      <Card className="border-border/60 shadow-sm overflow-hidden">
        <CardHeader className="bg-muted/30 py-4 sm:py-6">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <AlertCircle className="h-5 w-5 text-warning" />
            {t("simulation.instructions.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 sm:space-y-8 p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Exam Structure */}
            <div className="space-y-4">
              <h4 className="font-bold text-foreground text-sm sm:text-base border-b pb-2 flex items-center gap-2">
                <span className="w-1 h-4 bg-primary rounded-full" />
                <span>{t("simulation.instructions.examStructure")}</span>
              </h4>
              <ul className="space-y-3 text-sm">
                {filteredSettings.moduleOrder.map((module, index) => (
                  <li
                    key={module}
                    className="flex items-center gap-3 bg-muted/20 p-2 rounded-lg border border-border/40"
                  >
                    <Badge
                      variant="outline"
                      className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-background font-bold text-primary"
                    >
                      {index + 1}
                    </Badge>
                    <div className="flex flex-col">
                      <span className="font-bold text-foreground">
                        {t(`simulation.instructions.modules.${module}`)}
                      </span>
                      <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                        {filteredSettings.moduleTimings[module]}{" "}
                        {t("simulation.instructions.minutes")}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Rules */}
            <div className="space-y-4">
              <h4 className="font-bold text-foreground text-sm sm:text-base border-b pb-2 flex items-center gap-2">
                <span className="w-1 h-4 bg-orange-500 rounded-full" />
                <span>{t("simulation.instructions.rules")}</span>
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground px-1">
                <li className="flex gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t("simulation.instructions.timedSeparately")}</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t("simulation.instructions.noPausing")}</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t("simulation.instructions.answerAll")}</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>
                    {filteredSettings.pauseBetweenModules}{" "}
                    {t("simulation.instructions.breakBetween")}
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t("simulation.instructions.groupedByContext")}</span>
                </li>
              </ul>

              {/* Microphone Status */}
              {hasSpeakingModule && (
                <div className="mt-6 p-4 border rounded-xl bg-card shadow-inner border-border/60">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "w-2.5 h-2.5 rounded-full animate-pulse",
                          getMicrophoneStatusIndicatorClass(
                            microphonePermission,
                          ),
                        )}
                      />
                      <span className="font-bold text-xs uppercase tracking-tight">
                        {t("simulation.instructions.microphoneStatus")}
                      </span>
                    </div>
                    <span
                      className={cn(
                        "text-xs font-bold px-2 py-0.5 rounded-full border",
                        getMicrophoneStatusBadgeClass(microphonePermission),
                      )}
                    >
                      <span className="flex items-center gap-1 uppercase tracking-tighter">
                        {getMicrophoneStatusText(microphonePermission)}
                      </span>
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    {getMicrophoneDescriptionText(microphonePermission)}
                  </p>
                  {/* First-timer affordance: reassures users that a mic check step is coming */}
                  <p className="mt-2 text-[11px] text-primary/80 font-medium">
                    {t("simulation.instructions.micTestHint", {
                      defaultValue:
                        "You'll be able to test your mic before your first question.",
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>

          <Separator className="bg-border/40" />

          {/* Total Duration and Start Button */}
          <div className="text-center space-y-6">
            <div className="bg-muted/30 px-4 py-3 rounded-full inline-flex items-center gap-2">
              <span className="text-xs sm:text-sm text-muted-foreground font-medium">
                {t("simulation.instructions.totalDuration")}
              </span>
              <span className="font-bold text-sm sm:text-base text-foreground">
                {formatDuration(totalDuration)}
              </span>
            </div>

            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={onStartSimulation}
                disabled={isLoading}
                size="lg"
                className="w-full sm:w-auto sm:min-w-[280px] h-12 sm:h-14 text-sm sm:text-base font-bold shadow-xl shadow-primary/10 transition-all active:scale-95"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("simulation.instructions.preparing")}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Play className="h-4 w-4 fill-current" />
                    {t("simulation.instructions.startSimulation")}
                  </span>
                )}
              </Button>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {t("simulation.instructions.agreement")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
